import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Percent } from "lucide-react";
import { AdminTable, type ColDef } from "@/components/admin/shared/AdminTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { ChangeSalesPriceModal } from "@/components/admin/shared/ChangeSalesPriceModal";
import { DeleteConfirmModal } from "@/components/admin/shared/DeleteConfirmModal";
import { PageHeader, AddButton, SaveButton, CancelButton, ViewButton, EditButton, DeleteButton } from "@/components/admin/shared/PageHeader";
import { TablePagination } from "@/components/admin/shared/TablePagination";
import { TableSearch } from "@/components/admin/shared/TableSearch";
import { PennField, PennInput } from "@/components/ui";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { pemserterPriceSchema, type PemserterPriceFormData } from "@/types/admin/pemserter.types";
import {
  useCreatePemserterPriceListEur,
  useDeletePemserterPriceListEur,
  usePemserterPriceListEur,
  usePemserterPriceListEurDetail,
  useUpdatePemserterPriceListEur,
} from "@/hooks/usePemserterPriceListEur";
import {
  useCreatePemserterPriceListUsd,
  useDeletePemserterPriceListUsd,
  usePemserterPriceListUsd,
  usePemserterPriceListUsdDetail,
  useUpdatePemserterPriceListUsd,
} from "@/hooks/usePemserterPriceListUsd";
import {
  type PemserterPriceListEur,
} from "@/api/pemserterPriceListEur";
import {
  type PemserterPriceListUsd,
} from "@/api/pemserterPriceListUsd";
import { useApplyPriceUpdate, usePriceUpdateSummary } from "@/hooks/usePriceUpdate";
import { formatDecimal2dp, formatMoney } from "@/utils/dataset-money";

type PemItem = {
  _id: number;
  itemNumber: string;
  itemName: string;
  customerSalesPrice: number;
  distributorNetPrice: number;
  discountRate: number;
  discountPercent: number;
  /** Untouched numeric(14,6)/(18,10) wire strings — the table renders these
   *  (2dp via `dataset-money`), while the `number` fields above only prefill
   *  the edit form. Money never round-trips through a float for display (§L). */
  customerSalesPriceRaw: string | null;
  distributorNetPriceRaw: string | null;
  discountRateRaw: string | null;
};
type Props = { currency: "EUR" | "USD" };
type ModalMode = "add" | "edit" | "view";
type ModalState = { mode: ModalMode; item?: PemItem } | null;

const blank: PemserterPriceFormData = {
  itemNumber: "", itemName: "",
  customerSalesPrice: 0, discountRate: 0,
};

/** Render a numeric value verbatim without float coercion artefacts (§L). */
const num = (v: number | string | null | undefined): string => {
  if (v === null || v === undefined || v === "") return "0";
  return String(v);
};

/**
 * Live preview of the two always-derived fields:
 *   Distributor Net Price = Customer Sales Price × (1 − Discount Rate)  (2 dp)
 *   Discount Percent      = Discount Rate × 100                         (4 dp)
 * Blank when an input is missing/invalid (per spec). The backend (svc-04,
 * Decimal.js) is authoritative and recomputes these on save; the table then
 * refetches, so this is display-only — never the persisted source of truth.
 */
const computeDerived = (sales: unknown, rate: unknown): { net: string; percent: string } => {
  const s = Number(sales);
  const r = Number(rate);
  const salesOk = sales !== "" && sales !== null && sales !== undefined && Number.isFinite(s) && s >= 0;
  const rateOk = rate !== "" && rate !== null && rate !== undefined && Number.isFinite(r) && r >= 0 && r <= 1;
  if (!salesOk || !rateOk) return { net: "", percent: "" };
  return { net: (s * (1 - r)).toFixed(2), percent: (r * 100).toFixed(4) };
};

/** Narrow a table row to the operator-editable form fields. */
const toForm = (i: PemItem): PemserterPriceFormData => ({
  itemNumber: i.itemNumber,
  itemName: i.itemName,
  customerSalesPrice: i.customerSalesPrice,
  discountRate: i.discountRate,
});

const apiErrorMessage = (err: unknown, fallback: string): string => {
  if (err && typeof err === "object" && "response" in err) {
    const data = (err as { response?: { data?: { error?: { message?: string } } } }).response?.data;
    if (data?.error?.message) return data.error.message;
  }
  return err instanceof Error ? err.message : fallback;
};

/** Map a price-list API row (EUR or USD) -> the shared form/table item shape. */
const rowToItem = (
  r: PemserterPriceListEur | PemserterPriceListUsd,
  currency: "EUR" | "USD",
): PemItem => {
  const sales = currency === "EUR"
    ? (r as PemserterPriceListEur).customerSalesPriceEur
    : (r as PemserterPriceListUsd).customerSalesPriceUsd;
  const net = currency === "EUR"
    ? (r as PemserterPriceListEur).distributorNetPriceEur
    : (r as PemserterPriceListUsd).distributorNetPriceUsd;
  return {
    _id: r.rowId,
    itemNumber: r.itemNumber ?? "",
    itemName: r.itemName ?? "",
    customerSalesPrice: sales ? Number(sales) : 0,
    distributorNetPrice: net ? Number(net) : 0,
    discountRate: r.discountRate ? Number(r.discountRate) : 0,
    discountPercent: r.discountPercent ? Number(r.discountPercent) : 0,
    customerSalesPriceRaw: sales ?? null,
    distributorNetPriceRaw: net ?? null,
    discountRateRaw: r.discountRate ?? null,
  };
};

export const PemserterPage = ({ currency }: Props) => {
  const isEur = currency === "EUR";
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const { searchInput, setSearchInput, search, clearSearch } = useDebouncedSearch(() => setPageIndex(0));

  const listParams = useMemo(
    () => ({ page: pageIndex + 1, limit: pageSize, search: search || undefined }),
    [pageIndex, pageSize, search],
  );

  // Both currencies' hooks are called unconditionally (React rules); the
  // disabled-by-currency variants stay idle. We then pick the active one.
  const eurQuery = usePemserterPriceListEur(listParams);
  const usdQuery = usePemserterPriceListUsd(listParams);
  const query = isEur ? eurQuery : usdQuery;

  const eurCreate = useCreatePemserterPriceListEur();
  const usdCreate = useCreatePemserterPriceListUsd();
  const eurUpdate = useUpdatePemserterPriceListEur();
  const usdUpdate = useUpdatePemserterPriceListUsd();
  const eurDelete = useDeletePemserterPriceListEur();
  const usdDelete = useDeletePemserterPriceListUsd();

  const items = useMemo(
    () => (query.data?.data ?? []).map((r) => rowToItem(r, currency)),
    [query.data, currency],
  );
  const pagination = query.data?.pagination;
  const total = pagination?.total ?? 0;
  const hasNext = pagination ? pagination.page < pagination.totalPages : false;

  const [modal, setModal] = useState<ModalState>(null);
  const [deleteTarget, setDeleteTarget] = useState<PemItem | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // Bulk "Change Customer Sales Price" dialog state.
  const [priceChangeOpen, setPriceChangeOpen] = useState(false);
  const [bulkApplying, setBulkApplying] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);

  // No bulk endpoint exists (svc-04 is per-row CRUD): fetch every row, scale the
  // sales price by (1 ± %/100), and PUT each. Distributor Net Price is recomputed
  // from the new sales price; the backend (Decimal.js) stays authoritative — same
  // contract as the single-record save (§J).
  // Bulk price update goes through a single transactional POST that the
  // backend (svc-04 PriceUpdateService) drains in one round-trip and
  // audits in penn_config.price_update_log.
  const applyMutation = useApplyPriceUpdate();
  const summaryQuery = usePriceUpdateSummary(priceChangeOpen);
  const totalForModal = summaryQuery.data?.totalRecords ?? total;

  const applyPriceChange = async (
    percent: number,
    direction: "increase" | "decrease",
  ) => {
    setBulkError(null);
    setBulkApplying(true);
    try {
      await applyMutation.mutateAsync({
        currency,
        body: { percentage: percent, direction },
      });
      setPriceChangeOpen(false);
    } catch (e) {
      setBulkError(apiErrorMessage(e, "Failed to apply price change."));
    } finally {
      setBulkApplying(false);
    }
  };

  // Detail prefill for Edit/View
  const editingRowId = modal?.mode !== "add" ? (modal?.item?._id ?? null) : null;
  usePemserterPriceListEurDetail(isEur ? editingRowId : null);
  usePemserterPriceListUsdDetail(!isEur ? editingRowId : null);

  const label = `PEMSERTER Price List (${currency})`;

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm<PemserterPriceFormData>({
    resolver: zodResolver(pemserterPriceSchema),
  });

  // Distributor Net Price + Discount Percent are derived, never edited. Recompute
  // them live as the operator types so the disabled fields preview the result.
  const derived = computeDerived(watch("customerSalesPrice"), watch("discountRate"));

  const openAdd = () => { setFormError(null); reset(blank); setModal({ mode: "add" }); };
  const openEdit = (item: PemItem) => { setFormError(null); reset(toForm(item)); setModal({ mode: "edit", item }); };
  const openView = (item: PemItem) => { setFormError(null); reset(toForm(item)); setModal({ mode: "view", item }); };
  const closeModal = () => { setModal(null); setFormError(null); };

  const onSubmit = (data: PemserterPriceFormData) => {
    setFormError(null);

    // Derived fields are computed here for an accurate optimistic shape; the
    // backend (svc-04) recomputes them authoritatively and ignores these.
    const { net, percent } = computeDerived(data.customerSalesPrice, data.discountRate);

    // Build the currency-specific body (DB field names per spec).
    const prices = isEur
      ? {
          customerSalesPriceEur: String(data.customerSalesPrice),
          distributorNetPriceEur: net,
        }
      : {
          customerSalesPriceUsd: String(data.customerSalesPrice),
          distributorNetPriceUsd: net,
        };
    const common = {
      itemName: data.itemName.trim(),
      ...prices,
      discountRate: String(data.discountRate),
      discountPercent: percent,
    };

    if (modal?.mode === "edit" && modal.item) {
      const mutation = isEur ? eurUpdate : usdUpdate;
      mutation.mutate(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        { rowId: modal.item._id, body: { itemNumber: data.itemNumber, ...common } } as any,
        {
          onSuccess: closeModal,
          onError: (e) => setFormError(apiErrorMessage(e, "Failed to save record.")),
        },
      );
    } else {
      const mutation = isEur ? eurCreate : usdCreate;
      mutation.mutate(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        { itemNumber: data.itemNumber.trim(), ...common } as any,
        {
          onSuccess: closeModal,
          onError: (e) => setFormError(apiErrorMessage(e, "Failed to create record.")),
        },
      );
    }
  };

  const isView = modal?.mode === "view";
  const saving =
    eurCreate.isPending || usdCreate.isPending || eurUpdate.isPending || usdUpdate.isPending;

  const cols: ColDef<PemItem>[] = [
    { header: "itemNumber", key: "itemNumber" },
    { header: "itemName", key: "itemName" },
    // 2dp en-US cells (same shared formatter as Sherex Options); `title`
    // carries the untouched wire string so display rounding never hides the
    // stored numeric(14,6)/(18,10) value (§L).
    {
      header: `customerSalesPrice${currency}`,
      render: (i) => (
        <span className="whitespace-nowrap tabular-nums" title={i.customerSalesPriceRaw ?? undefined}>
          {formatMoney(i.customerSalesPriceRaw, currency)}
        </span>
      ),
    },
    {
      header: `distributorNetPrice${currency}`,
      render: (i) => (
        <span className="whitespace-nowrap tabular-nums" title={i.distributorNetPriceRaw ?? undefined}>
          {formatMoney(i.distributorNetPriceRaw, currency)}
        </span>
      ),
    },
    {
      header: "discountRate",
      render: (i) => (
        <span className="whitespace-nowrap tabular-nums" title={i.discountRateRaw ?? undefined}>
          {formatDecimal2dp(i.discountRateRaw)}
        </span>
      ),
    },
    { header: "discountPercent", render: (i) => `${num(i.discountPercent)}%` },
    { header: "Actions", render: (i) => <div className="flex space-x-2"><ViewButton onClick={() => openView(i)} /><EditButton onClick={() => openEdit(i)} /><DeleteButton onClick={() => setDeleteTarget(i)} /></div> },
  ];

  const titleMap = {
    add: `Add ${label} record`,
    edit: `Edit ${label} record`,
    view: `View ${label} record`,
  };

  return (
    <>
      <PageHeader
        title={`Dataset: ${label}`}
        subtitle={`Record-level editor for priceList (id: itemNumber). · ${total} records`}
        action={
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => { setBulkError(null); setPriceChangeOpen(true); }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-penn-500 hover:bg-penn-700 active:bg-penn-500 text-white rounded-lg shadow-sm shadow-penn-100 dark:shadow-penn-900/40 transition-all duration-150 cursor-pointer text-body-medium"
            >
              <Percent size={15} />
              Change Customer Sales Price
            </button>
            <AddButton onClick={openAdd} />
          </div>
        }
      />

      {query.isError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-body">
          Failed to load price list{query.error instanceof Error ? `: ${query.error.message}` : "."}
        </div>
      )}

      <TableSearch
        value={searchInput}
        onChange={setSearchInput}
        onClear={clearSearch}
        placeholder="Search item number…"
      />

      <AdminTable columns={cols} rows={items} keyExtractor={(i) => i._id} loading={query.isLoading} />

      <TablePagination
        pageIndex={pageIndex}
        pageSize={pageSize}
        rowsOnPage={items.length}
        hasNext={hasNext}
        busy={query.isFetching}
        onPrev={() => setPageIndex((p) => Math.max(0, p - 1))}
        onNext={() => setPageIndex((p) => p + 1)}
        onPageSizeChange={(size) => { setPageSize(size); setPageIndex(0); }}
      />

      <AdminModal
        open={modal !== null}
        onClose={closeModal}
        title={modal ? titleMap[modal.mode] : ""}
        maxWidth="max-w-2xl"
        footer={
          isView
            ? <CancelButton onClick={closeModal} />
            : <><CancelButton onClick={closeModal} /><SaveButton onClick={handleSubmit(onSubmit)} label={saving ? "Saving…" : "Save"} /></>
        }
      >
        <form className="grid grid-cols-1 sm:grid-cols-2 gap-4" onSubmit={handleSubmit(onSubmit)}>
          {formError && (
            <div className="col-span-1 sm:col-span-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-body">
              {formError}
            </div>
          )}
          <PennField label="Item Number" htmlFor="itemNumber" required={!isView} error={!isView ? errors.itemNumber?.message : undefined}>
            <PennInput id="itemNumber" disabled={isView || modal?.mode === "edit"} error={!isView && !!errors.itemNumber} {...register("itemNumber")} />
          </PennField>
          <PennField label="Item Name" htmlFor="itemName" required={!isView} error={!isView ? errors.itemName?.message : undefined}>
            <PennInput id="itemName" disabled={isView} error={!isView && !!errors.itemName} {...register("itemName")} />
          </PennField>
          <PennField label={`Customer Sales Price (${currency})`} htmlFor="customerSalesPrice" required={!isView} error={!isView ? errors.customerSalesPrice?.message : undefined}>
            <PennInput id="customerSalesPrice" type="number" step="0.01" disabled={isView} error={!isView && !!errors.customerSalesPrice} {...register("customerSalesPrice")} />
          </PennField>
          <PennField label="Discount Rate" htmlFor="discountRate" required={!isView} error={!isView ? errors.discountRate?.message : undefined} hint={!isView && !errors.discountRate ? "Decimal between 0 and 1 (e.g. 0.20 = 20%)" : undefined}>
            <PennInput id="discountRate" type="number" step="0.0001" min={0} max={1} disabled={isView} error={!isView && !!errors.discountRate} {...register("discountRate")} />
          </PennField>
          <PennField label={`Distributor Net Price (${currency})`} htmlFor="distributorNetPrice" hint="Auto-calculated · Sales Price × (1 − Discount Rate)">
            <PennInput id="distributorNetPrice" type="text" disabled readOnly value={derived.net} placeholder="—" />
          </PennField>
          <PennField label="Discount Percent" htmlFor="discountPercent" hint="Auto-calculated · Discount Rate × 100">
            <PennInput id="discountPercent" type="text" disabled readOnly value={derived.percent ? `${derived.percent}%` : ""} placeholder="—" />
          </PennField>
        </form>
      </AdminModal>

      <ChangeSalesPriceModal
        open={priceChangeOpen}
        onClose={() => setPriceChangeOpen(false)}
        currency={currency}
        recordCount={totalForModal}
        applying={bulkApplying}
        error={bulkError}
        onApply={applyPriceChange}
        lastUpdate={isEur ? summaryQuery.data?.eur : summaryQuery.data?.usd}
        loadingSummary={summaryQuery.isLoading}
      />

      <DeleteConfirmModal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (!deleteTarget) return;
          const mutation = isEur ? eurDelete : usdDelete;
          mutation.mutate(deleteTarget._id, {
            onSettled: () => setDeleteTarget(null),
          });
        }}
        itemLabel={deleteTarget?.itemName}
      />
    </>
  );
};
